import type { SaleReturnItemEntity } from '@/features/sale/domain/entities/SaleItemEntity.js';

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(row: Record<string, unknown>, key: string, required = false): string | null {
  const value = row[key];
  if (typeof value === 'string' && (!required || value.length > 0)) return value;
  if (!required && (value === null || value === undefined)) return null;
  throw new Error(`Invalid sale return field: ${key}`);
}

/** Fail closed: malformed provenance must never become an empty/fabricated return. */
export function parseSaleReturnItems(value: unknown): SaleReturnItemEntity[] {
  if (!record(value) || !Array.isArray(value.items)) throw new Error('Invalid sale return details');
  const ids = new Set<string>();
  return value.items.map((row: unknown) => {
    if (!record(row)) throw new Error('Invalid sale return line');
    const id = text(row, 'id', true)!;
    const unitQty = row.unit_qty;
    if (ids.has(id) || typeof unitQty !== 'number' || !Number.isSafeInteger(unitQty) || unitQty <= 0) {
      throw new Error('Invalid sale return identity or quantity');
    }
    ids.add(id);
    const batchCodeId = text(row, 'batch_code_id');
    const batchCode = text(row, 'batch_code');
    const mfgDate = text(row, 'mfg_date');
    const expDate = text(row, 'exp_date');
    if (batchCodeId && (!batchCode?.trim() || !mfgDate || !expDate)) throw new Error('Incomplete original sale lot metadata');
    return {
      id,
      assetId: text(row, 'asset_id', true)!,
      unitQty,
      originalStockItemId: text(row, 'original_stock_item_id'),
      sourceBatchId: text(row, 'source_batch_id'),
      batchCodeId,
      batchCode,
      mfgDate,
      expDate,
      productName: text(row, 'product_name', true)!,
      sku: text(row, 'sku'),
      unit: text(row, 'unit'),
    };
  });
}
